import Box from "@/common/components/Box";
import CableStatus from "@/common/components/CableStatus";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import SmallBadge from "@/common/components/SmallBadge";
import { useActionCable } from "@/common/hooks/cable/useActionCable";
import { cn } from "@/common/utils/cn";
import { getDateDiffFormatted } from "@/common/utils/date";
import { formatDateTime } from "@/common/utils/formatters";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarCheckIcon, CalendarXIcon, ClockIcon, FilesIcon, GlobeIcon, ScanEyeIcon, UserCheckIcon } from "lucide-react";
import { getUserTimezoneFormatted } from "@/common/utils/dateUtils";
import { useParams } from "react-router-dom";
import { useMaintenanceEstablishmentChoiceAuctionEntry } from "../../hooks/useMaintenanceEstablishmentChoiceAuctionEntry";
import { useMaintenanceEstablishmentChoiceAuctionEntryUpdatedSummary } from "../../hooks/useMaintenanceEstablishmentChoiceAuctionEntryUpdatedSummary";
import AuctionProposal from "./components/AuctionProposal/AuctionProposal";
import AuctionTimer from "./components/AuctionTimer";
import CreateAuctionProposalButton from "./components/CreateAuctionProposalButton";
import FollowAuction from "./components/FollowAuction";
import MaintenanceEstablishmentChoiceAuctionEntriesShowLoading from "./components/MaintenanceEstablishmentChoiceAuctionEntriesShowLoading";
import MaintenanceEstablishmentChoiceEntryAuctionHeader from "./components/MaintenanceEstablishmentChoiceEntryAuctionHeader";
import UnfollowAuction from "./components/UnfollowAuction";
import { auctionEntryKind } from "./constants/auctionEntryKind.constant";
import { auctionEntryStatus } from "./constants/auctionEntryStatus.constant";

const MaintenanceEstablishmentChoiceAuctionEntriesShow = () => {
  const { auctionEntryId } = useParams();

  const queryClient = useQueryClient();

  const {
    data: auctionSummary,
    isPending: auctionSummaryIsPending,
    refetch: refetchAuctionSummary,
    error: auctionSummaryError = {} as any,
  } = useMaintenanceEstablishmentChoiceAuctionEntryUpdatedSummary({
    auctionEntryId,
  });

  const {
    data: auctionEntry,
    dataUpdatedAt: auctionEntryUpdatedAt,
  } = useMaintenanceEstablishmentChoiceAuctionEntry({
    auctionEntryId,
  });

  const isAuctionFinished = auctionSummary?.status === 'completed' || (auctionSummary?.finish_at
    ? (new Date(auctionSummary?.finish_at)).getTime() < (new Date()).getTime()
    : true);

  const isAuctionActive = auctionSummary?.status === 'in_progress' && !auctionSummary?.paused_at && !isAuctionFinished;

  const isAuctionCanceled = auctionSummary?.status === 'canceled';

  const isAuctionPaused = auctionSummary?.status === 'in_progress' && !!auctionSummary?.paused_at && !isAuctionFinished;

  const {
    status,
  } = useActionCable({
    channel: 'Establishment::Maintenance::EstablishmentChoice::AuctionEntries::BiddingChannel',
    params: {
      auction_entry_id: auctionEntryId,
    },
    enabled: !!auctionEntryId && !isAuctionFinished,
    onMessage: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenanceEstablishmentChoiceAuctionEntryUpdatedSummary', auctionEntryId] });
      queryClient.invalidateQueries({ queryKey: ['maintenanceEstablishmentChoiceAuctionEntry', auctionEntryId] });
    },
  });

  return (
    <>
      <div className="inner-screen">
        <MaintenanceEstablishmentChoiceEntryAuctionHeader
          auctionSummary={auctionSummary}
          auctionSummaryIsPending={auctionSummaryIsPending}
          actions={!isAuctionFinished && (
            <div className="flex gap-3">
              <CableStatus
                status={status}
              />
            </div>
          ) || <></>}
        />

        {auctionSummaryIsPending && <MaintenanceEstablishmentChoiceAuctionEntriesShowLoading />}

        {!auctionSummaryIsPending && (
          <ErrorMessageWrapper
            className="flex-1"
            refetchFunction={refetchAuctionSummary}
            statusCode={auctionSummaryError?.status}
            notFoundFallback="/maintenance-establishment-choice/auction-entries"
            notFoundButtonText="Ir para Leilões"
          >
            <div className="flex-1 flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <Box className={cn('flex-1 p-3 rounded-lg flex flex-col gap-2', isAuctionCanceled && 'bg-danger-op', isAuctionPaused && 'bg-yellow-op-2')}>
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div>
                      <p className="text-base font-bold">
                        Leilão
                      </p>

                      <p className={cn('text-xs text-valid', isAuctionCanceled && 'text-danger')}>
                        {auctionSummary?.status && auctionEntryStatus[auctionSummary?.status]}
                      </p>
                    </div>

                    <div className="flex items-center gap-1 flex-wrap">
                      <SmallBadge
                        title="Tipo de leilão"
                        LeftIcon={ScanEyeIcon}
                        text={auctionSummary?.kind ? auctionEntryKind[auctionSummary?.kind] : '--'}
                      />

                      <SmallBadge
                        title="Duração do leilão"
                        LeftIcon={ClockIcon}
                        text={
                          isAuctionFinished || auctionSummary?.status === 'completed'
                            ? 'Finalizado'
                            : auctionSummary?.finish_at
                              ? `${getDateDiffFormatted(
                                auctionSummary?.finish_at ? new Date(auctionSummary?.finish_at) : new Date(),
                                auctionSummary?.start_at ? new Date(auctionSummary?.start_at) : new Date()
                              )}`
                              : '--'
                        }
                      />

                      <SmallBadge
                        title="Data de início"
                        LeftIcon={CalendarCheckIcon}
                        text={auctionSummary?.start_at ? formatDateTime(new Date(auctionSummary?.start_at)) : 'Imediato'}
                      />

                      <SmallBadge
                        title="Data de encerramento"
                        LeftIcon={CalendarXIcon}
                        text={auctionSummary?.finish_at ? formatDateTime(new Date(auctionSummary?.finish_at)) : '--'}
                      />

                      <SmallBadge
                        title="Fuso horário"
                        LeftIcon={GlobeIcon}
                        text={getUserTimezoneFormatted()}
                      />
                    </div>
                  </div>

                  <div className="flex items-end justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-1 flex-wrap">
                      <SmallBadge
                        LeftIcon={UserCheckIcon}
                        text={auctionSummary?.participants_count != null ? `${auctionSummary?.participants_count} Participando` : '--'}
                      />

                      <SmallBadge
                        LeftIcon={FilesIcon}
                        text={auctionSummary?.proposals_count != null ? `${auctionSummary?.proposals_count} Enviadas` : '--'}
                      />
                    </div>

                    <div className="flex items-center gap-1 flex-wrap">
                      {auctionSummary && !auctionSummary.your_proposal && (
                        auctionSummary.is_following ? (
                          <UnfollowAuction
                            auctionEntryId={auctionEntryId}
                          />
                        ) : (
                          <FollowAuction
                            auctionEntryId={auctionEntryId}
                          />
                        )
                      )}

                      {(isAuctionActive || isAuctionPaused) && (
                        <CreateAuctionProposalButton
                          auctionEntryId={auctionEntryId}
                          auctionSummary={auctionSummary}
                          disabled={isAuctionPaused}
                        />
                      )}
                    </div>
                  </div>
                </Box>

                {auctionSummary?.finish_at && (
                  <AuctionTimer
                    startDate={auctionSummary?.start_at ? new Date(auctionSummary?.start_at) : new Date()}
                    endDate={auctionSummary?.finish_at ? new Date(auctionSummary?.finish_at) : new Date()}
                    isPaused={!!auctionSummary?.paused_at}
                    isFinished={!!isAuctionFinished}
                    isCanceled={isAuctionCanceled}
                  />
                )}
              </div>

              <Box className="flex-1 overflow-auto thin-scroll p-3 rounded-lg flex flex-col gap-2">
                {auctionSummary?.kind === 'closed' ? (
                  auctionSummary?.your_proposal ? (
                    <AuctionProposal
                      proposal={auctionSummary.your_proposal}
                      yourProposal={auctionSummary.your_proposal}
                      auctionEntry={auctionEntry}
                      auctionEntryUpdatedAt={auctionEntryUpdatedAt}
                      auctionKind={auctionSummary?.kind}
                      directiveEntryType={auctionSummary?.directive_entry_type}
                    />
                  ) : (
                    <div className="flex-1 flex flex-col items-center justify-center gap-2">
                      <img src="/auction-empty-state.gif" className="h-40 sm:h-56 md:h-72 lg:h-96 w-auto" />

                      <p className="font-bold text-base">
                        Você ainda não enviou a sua proposta
                      </p>

                      {(isAuctionActive || isAuctionPaused) && (
                        <CreateAuctionProposalButton
                          auctionEntryId={auctionEntryId}
                          auctionSummary={auctionSummary}
                          disabled={isAuctionPaused}
                        />
                      )}
                    </div>
                  )
                ) : auctionSummary?.proposals && Object.values(auctionSummary?.proposals)?.length > 0 ? Object.values(auctionSummary?.proposals)?.map((proposal) => (
                  <AuctionProposal
                    key={proposal.id}
                    proposal={proposal}
                    yourProposal={auctionSummary?.your_proposal}
                    auctionEntry={auctionEntry}
                    auctionEntryUpdatedAt={auctionEntryUpdatedAt}
                    auctionKind={auctionSummary?.kind}
                    directiveEntryType={auctionSummary?.directive_entry_type}
                  />
                )) : (
                  <div className="flex-1 flex flex-col items-center justify-center gap-2">
                    <img src="/auction-empty-state.gif" className="h-40 sm:h-56 md:h-72 lg:h-96 w-auto" />

                    <p className="font-bold text-base">
                      Nenhuma proposta enviada, seja o primeiro a enviar sua proposta
                    </p>
                  </div>
                )}
              </Box>
            </div>
          </ErrorMessageWrapper>
        )}
      </div>
    </>
  );
}

export default MaintenanceEstablishmentChoiceAuctionEntriesShow;
